import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import {
  capitalizeFirstLetter,
  getFirstStartedAtTs,
  getLastEndedAtTs,
} from "@intelligems/ig-utils";
import { conversionStageReachedList } from "@src/components/AudienceFilters/utils";

import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
} from "../../store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "../../store/types/sitewideDashboardFilters";
import { fullDateTimeConverter } from "../../utils/dateConverters";

export type AnalyticsFilterReducerAction =
  | {
      type: "updateTestOrdersOnly";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateIsZScoreEnabled";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateDiscountIncludeType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateDeviceType";
      payload: Pick<IExperienceDashboardFilters, "deviceType">;
    }
  | {
      type: "updateCustomEvents";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateCustomEventsFilterType";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateCountryCodes";
      payload: Pick<IExperienceDashboardFilters, "countryCodes">;
    }
  | {
      type: "updateVisitorType";
      payload: Pick<IExperienceDashboardFilters, "visitorType">;
    }
  | {
      type: "updateUrlParam";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateLandingPage";
      payload: Pick<IExperienceDashboardFilters, "landingPage">;
    }
  | {
      type: "updateUserBehavior";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateUserBehaviorViews";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateReachedCheckoutStage";
      payload: { dashboardFilters: IExperienceDashboardFilters };
    }
  | {
      type: "updateChannelType";
      payload: Pick<IExperienceDashboardFilters, "channels">;
    }
  | {
      type: "updateEnd";
      payload: {
        endTime: Pick<IExperienceDashboardFilters, "endTime">;
        experience?: ExperienceFe;
        timezone: string;
      };
    }
  | {
      type: "updateStart";
      payload: {
        startTime: Pick<IExperienceDashboardFilters, "startTime">;
        experience?: ExperienceFe;
        timezone: string;
      };
    };

export function AnalyticsFilterReducer(
  draft: AnalyticsActiveFilterType[],
  action: AnalyticsFilterReducerAction
) {
  switch (action.type) {
    case "updateTestOrdersOnly":
      if (
        action.payload.dashboardFilters.testOrdersOnly === true &&
        !draft.some((chip) => chip.key === "testOrdersOnly")
      ) {
        draft = [
          ...draft,
          {
            key: "testOrdersOnly",
            value: true,
            label: "Target Products Only",
          },
        ];
      } else if (
        action.payload.dashboardFilters.testOrdersOnly === false &&
        draft.some((chip) => chip.key === "testOrdersOnly")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "testOrdersOnly";
        });
      }
      return draft;

    case "updateIsZScoreEnabled":
      if (action.payload.dashboardFilters.isZScoreEnabled === true) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "isZScoreEnabled";
          }),
          {
            key: "isZScoreEnabled",
            value: true,
            label: `Max Z-Score: ${action.payload.dashboardFilters.maxZScore}`,
          },
        ];
      } else if (
        action.payload.dashboardFilters.isZScoreEnabled === false &&
        draft.some((chip) => chip.key === "isZScoreEnabled")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "isZScoreEnabled";
        });
      }
      return draft;
    case "updateDiscountIncludeType":
      if (
        action.payload.dashboardFilters.discountIncludeType === 2 &&
        !draft.some((chip) => chip.key === "discountIncludeType")
      ) {
        draft = [
          ...draft,
          {
            key: "discountIncludeType",
            value: 2,
            label: "IG Offer Only",
          },
        ];
      } else if (
        action.payload.dashboardFilters.discountIncludeType === 1 &&
        draft.some((chip) => chip.key === "discountIncludeType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "discountIncludeType";
        });
      }
      return draft;

    case "updateDeviceType":
      if (
        (action.payload.deviceType === "any" ||
          action.payload.deviceType === null) &&
        draft.some((chip) => chip.key === "deviceType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "deviceType";
        });
      } else if (
        action.payload.deviceType === "desktop" ||
        action.payload.deviceType === "mobile"
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "deviceType";
          }),
          {
            key: "deviceType",
            value: action.payload.deviceType,
            label: `${capitalizeFirstLetter(action.payload.deviceType)}`,
          },
        ];
      }
      return draft;
    case "updateCustomEvents":
      if (
        action.payload.dashboardFilters.customEvents?.length === 0 &&
        draft.some((chip) => chip.key === "customEvents")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "customEvents";
        });
      } else if (
        action.payload.dashboardFilters.customEvents?.length !== 0 &&
        !draft.some((chip) => chip.key === "customEvents")
      ) {
        let customEventsLabel: string | undefined = "";
        if (
          action.payload.dashboardFilters.customEvents &&
          action.payload.dashboardFilters.customEvents.length > 2
        ) {
          customEventsLabel = action.payload.dashboardFilters.customEvents
            .slice(0, 2)
            .join(", ");
          customEventsLabel =
            customEventsLabel +
            `, ... + ${
              action.payload.dashboardFilters.customEvents.length - 2
            } more`;
        } else {
          customEventsLabel =
            action.payload.dashboardFilters.customEvents?.join(", ");
        }
        draft = [
          ...draft,
          {
            key: "customEvents",
            value: action.payload.dashboardFilters.customEvents,
            label: `Custom Events: ${customEventsLabel}`,
          },
        ];
      }
      return draft;
    case "updateCountryCodes":
      if (
        action.payload.countryCodes?.length === 0 &&
        draft.some((chip) => chip.key === "countryCodes")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "countryCodes";
        });
      } else if (action.payload.countryCodes?.length !== 0) {
        let countryCodesLabel: string | undefined = "";

        if (
          action.payload.countryCodes &&
          action.payload.countryCodes.length > 3
        ) {
          countryCodesLabel = action.payload.countryCodes
            .slice(0, 3)
            .join(", ");
          countryCodesLabel =
            countryCodesLabel + ` + ${action.payload.countryCodes.length - 3}`;
        } else {
          countryCodesLabel = action.payload.countryCodes?.join(", ");
        }

        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "countryCodes";
          }),
          {
            key: "countryCodes",
            value: action.payload.countryCodes,
            label: `${countryCodesLabel}`,
          },
        ];
      }
      return draft;
    case "updateVisitorType":
      if (
        (action.payload.visitorType === "any" ||
          action.payload.visitorType === null) &&
        draft.some((chip) => chip.key === "visitorType")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "visitorType";
        });
      } else if (
        action.payload.visitorType === "new" ||
        action.payload.visitorType === "returning"
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "visitorType";
          }),
          {
            key: "visitorType",
            value: action.payload.visitorType,
            label: `${capitalizeFirstLetter(action.payload.visitorType)}`,
          },
        ];
      }
      return draft;

    case "updateReachedCheckoutStage":
      if (
        (action.payload.dashboardFilters.reachedCheckoutStage === "any" ||
          action.payload.dashboardFilters.reachedCheckoutStage === null) &&
        draft.some((chip) => chip.key === "reachedCheckoutStage")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "reachedCheckoutStage";
        });
      } else if (
        action.payload.dashboardFilters.reachedCheckoutStage !== null &&
        action.payload.dashboardFilters.reachedCheckoutStage !== "any"
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "reachedCheckoutStage";
          }),
          {
            key: "reachedCheckoutStage",
            value: action.payload.dashboardFilters.reachedCheckoutStage,
            label:
              conversionStageReachedList.find(
                (entry) =>
                  entry.key ===
                  action.payload.dashboardFilters.reachedCheckoutStage
              )?.label || "",
          },
        ];
      }
      return draft;

    case "updateUrlParam":
      if (
        !action.payload.dashboardFilters.urlParam?.queryParam &&
        draft.some((chip) => chip.key === "urlParam")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "urlParam";
        });
      } else if (action.payload.dashboardFilters.urlParam?.queryParam) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "urlParam";
          }),
          {
            key: "urlParam",
            value: action.payload.dashboardFilters.urlParam,
            label: "Url Param Enabled",
          },
        ];
      }

      return draft;
    case "updateLandingPage": {
      const landingPage = action.payload.landingPage;
      const hasLandingPage = draft.some((chip) => chip.key === "landingPage");
      const isNullFilter = ["isNull", "isNotNull"].includes(
        landingPage?.landingPageUrlPathFilterType || ""
      );

      if (
        hasLandingPage &&
        (!landingPage ||
          !landingPage.landingPageUrlPathFilterType ||
          (!landingPage.landingPageUrlPath && !isNullFilter))
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "landingPage";
        });
      } else if (
        !hasLandingPage &&
        landingPage?.landingPageUrlPathFilterType &&
        (landingPage?.landingPageUrlPath || isNullFilter)
      ) {
        draft = [
          ...draft,
          {
            key: "landingPage",
            value: landingPage.landingPageUrlPath || "null",
            label: "Landing Page Enabled",
          },
        ];
      }
      return draft;
    }
    case "updateUserBehavior":
      if (
        !action.payload.dashboardFilters.userBehavior?.addedToCart &&
        !action.payload.dashboardFilters.userBehavior?.viewedCheckout &&
        !action.payload.dashboardFilters.userBehavior?.viewedCollectionPage &&
        !action.payload.dashboardFilters.userBehavior?.viewedProductPage &&
        draft.some((chip) => chip.key === "userBehavior")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "userBehavior";
        });
      } else if (
        action.payload.dashboardFilters.userBehavior?.addedToCart ||
        action.payload.dashboardFilters.userBehavior?.viewedCheckout ||
        action.payload.dashboardFilters.userBehavior?.viewedCollectionPage ||
        action.payload.dashboardFilters.userBehavior?.viewedProductPage
        // &&
        // !draft.some((chip) => chip.key === "userBehavior")
      ) {
        let filterString = "";
        let hasViewedProductPage = false;
        if (action.payload.dashboardFilters.userBehavior?.viewedProductPage) {
          filterString = "Visited Product Page";
          hasViewedProductPage = true;
        }
        if (
          action.payload.dashboardFilters.userBehavior?.viewedCollectionPage
        ) {
          if (hasViewedProductPage) {
            filterString = filterString + " & Collection";
          } else {
            filterString = "Visited Collection";
          }
        }
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "userBehavior";
          }),
          {
            key: "userBehavior",
            value: action.payload.dashboardFilters.userBehavior,
            label: filterString,
          },
        ];
      }

      return draft;

    case "updateUserBehaviorViews":
      if (
        !action.payload.dashboardFilters.userBehavior?.viewedCollectionPage &&
        !action.payload.dashboardFilters.userBehavior?.viewedProductPage &&
        draft.some((chip) => chip.key === "userBehavior")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "userBehavior";
        });
      } else if (
        action.payload.dashboardFilters.userBehavior?.viewedCollectionPage ||
        action.payload.dashboardFilters.userBehavior?.viewedProductPage
      ) {
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "userBehavior";
          }),
          {
            key: "userBehavior",
            value: action.payload.dashboardFilters.userBehavior,
            label: `Viewed ${action.payload.dashboardFilters.userBehavior.viewedCollectionPage ? "Collection" : ""} ${action.payload.dashboardFilters.userBehavior.viewedCollectionPage && action.payload.dashboardFilters.userBehavior.viewedProductPage ? "&" : ""} ${action.payload.dashboardFilters.userBehavior.viewedProductPage ? "Product" : ""} Page`,
          },
        ];
      }

      return draft;

    case "updateChannelType":
      if (
        !action.payload.channels?.length &&
        draft.some((chip) => chip.key === "channels")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "channels";
        });
      } else if (
        (action.payload.channels ?? []).length > 0 &&
        action.payload.channels
      ) {
        let channelTypeLabel: string | undefined = "";
        if (action.payload.channels && action.payload.channels.length > 3) {
          channelTypeLabel = action.payload.channels.slice(0, 3).join(", ");
          channelTypeLabel =
            channelTypeLabel + ` + ${action.payload.channels.length - 3}`;
        } else {
          channelTypeLabel = action.payload.channels?.join(", ");
        }
        draft = [
          ...draft.filter((chip) => {
            return chip.key !== "channels";
          }),
          {
            key: "channels",
            value: action.payload.channels,
            label: `${channelTypeLabel}`,
          },
        ];
      }
      return draft;
    case "updateEnd":
      if (action.payload.endTime.endTime) {
        const initialEndVal: AnalyticsActiveFilterType = {
          key: "endTime",
          value: action.payload.endTime.endTime,
          label: `End Time: ${fullDateTimeConverter(
            action.payload.endTime.endTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };
        // if experience is ended (endedAts)
        if (action.payload.experience) {
          const latestEndedAtTs = getLastEndedAtTs(
            action.payload.experience.experienceActions
          );

          // if filter already set
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            if (draft[currFilterVal].value !== latestEndedAtTs) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else if (
              // if filter was cleared
              latestEndedAtTs === action.payload.endTime.endTime
            ) {
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.endTime.endTime !== latestEndedAtTs) {
              draft = [...draft, initialEndVal];
            }
          }
        } else {
          if (draft.some((chip) => chip.key === "endTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "endTime"
            );
            // update filter
            if (draft[currFilterVal].value !== null) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? initialEndVal : chip;
              });
            } else {
              // remove filter
              draft = draft.filter((chip) => {
                return chip.key !== "endTime";
              });
            }
          } else {
            if (action.payload.endTime.endTime) {
              draft = [...draft, initialEndVal];
            }
          }
        }
      } else if (
        !action.payload.endTime.endTime &&
        draft.some((chip) => chip.key === "endTime")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "endTime";
        });
      }

      return draft;

    case "updateStart":
      if (action.payload.startTime.startTime) {
        const dashboardFilterVal: AnalyticsActiveFilterType = {
          key: "startTime",
          value: action.payload.startTime.startTime,
          label: `Start Time: ${fullDateTimeConverter(
            action.payload.startTime.startTime,
            action.payload.timezone,
            undefined,
            true
          )}`,
        };

        if (action.payload.experience) {
          const initialStartedAtTs = getFirstStartedAtTs(
            action.payload.experience.experienceActions
          );
          // if filter already set
          if (draft.some((chip) => chip.key === "startTime")) {
            const currFilterVal = draft.findIndex(
              (chip) => chip.key === "startTime"
            );
            // if start filter is updated
            if (initialStartedAtTs === action.payload.startTime.startTime) {
              draft = draft.filter((chip) => {
                return chip.key !== "startTime";
              });
            } else if (
              dashboardFilterVal.value !== draft[currFilterVal].value
            ) {
              draft = draft.map((chip, index) => {
                return index === currFilterVal ? dashboardFilterVal : chip;
              });
            }
          }
          // initial filter set
          else if (
            initialStartedAtTs !== action.payload.startTime.startTime &&
            !draft.some((chip) => chip.key === "startTime")
          ) {
            draft = [...draft, dashboardFilterVal];
          }
        }
      } else if (
        !action.payload.startTime.startTime &&
        draft.some((chip) => chip.key === "startTime")
      ) {
        draft = draft.filter((chip) => {
          return chip.key !== "startTime";
        });
      }

      return draft;
  }
}
